"use client";

import { deleteWishlistLineItem, notifyOutOfStockProduct } from "@/api/saleor/services/wishlist";
import {
  getCartCount,
  getPromotionBadgeName,
  getValueBasedOnLanguage,
  hasArrayElements,
  hasLensPower,
} from "@/apiUtils/commonUtils";
import {
  analyticBuyFlows,
  analyticEventSources,
  analyticEventTypes,
  cartErrors,
  ProductType,
  SaleorAttributes,
} from "@/apiUtils/constants";
import { buildProductPriceInfo, getSpecsForProductType } from "@/apiUtils/productUtils";
import CloseIcon from "@/assets/icons/close.svg?react";
import {
  FavoritesPageData,
  TranslationData,
  WishlistItem,
  WishlistResponse,
} from "@/common/lib/types";
import { CLink } from "@/components/common/customLink/CustomLink";
import NotifyInStock from "@/components/common/NotifyInStock";
import LensConfiguratorWrapper from "@/components/lcp/LCPWrapper";
import { applicableProductTypes } from "@/components/lcp/resource/config";
import ProductBadge from "@/components/productBadge/ProductBadge";
import RenderCurrencyInText from "@/components/common/RenderCurrencyInText";
import ProductPrice from "@/components/productPrice/ProductPrice";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useGlobalStore } from "@/store/store";
import { addToCartById } from "@/utils/cart.utils";

import NoImageFallback from "@/assets/images/no_image.svg";
import {
  getChannelIdInClient,
  getFieldValue,
  getLangFromLocale,
  isNonTransactionalCountry,
} from "@/utils/common";
import Image from "next/image";
import { useParams, useRouter } from "next/navigation";
import { Fragment, useMemo, useState } from "react";
import Button from "../../ui/button/Button";
import RemoveFavorite from "./RemoveFavorite";
import VtoModal from "@/components/pdp/VtoModal";
import { extractValueBySlug } from "@/utils/product.utils";
import CldImage from "@/components/ui/cldImg/CldImage";
import { analyticEvents, handlePostAnalytics } from "@/apiUtils/analyticsUtils";
import { getCloudinaryURL } from "@/utils/cloudinary";
import { ProductAttributeOverride } from "@/common/lib/types";

interface IProps {
  favoritesData: FavoritesPageData;
  item: WishlistItem;
  translations: TranslationData;
  removeProductFromLocalWishlist: (wishlistResponse: WishlistResponse) => void;
  isVtoEnabledLocation?: boolean;
  wishlistId?: string;
  automationPayload: string[];
  index?: number;
  productAttributeOverrides?: ProductAttributeOverride[];
}

const FavoritesItemCard = ({
  item,
  favoritesData,
  removeProductFromLocalWishlist,
  translations,
  isVtoEnabledLocation,
  wishlistId,
  automationPayload,
  index,
  productAttributeOverrides,
}: IProps) => {
  //states
  const [openRemoveFavorite, setOpenRemoveFavorite] = useState(false);
  const [openNotifyInStock, setOpenNotifyInStock] = useState(false);
  const [vtoOpen, setVtoOpen] = useState(false);
  const pdpContent = translations?.pdp || {};

  const { cartCount, user, removeProductFromWishlist, updateCartCount } = useGlobalStore(
    (state) => state,
  );

  //const
  const source = `${analyticEventSources.WISHLIST} - ${favoritesData?.title}`;
  const sourceId = favoritesData?.id;
  const { isDefaultVariant, variant } = item;
  const { product, pricing, quantityAvailable, badgeList, landingPageBaseUrl } = variant || {};
  const { productType, slug } = product || {};
  const favoritesStaticText = translations?.favorites || {};

  //hooks
  const { locale } = useParams();
  const router = useRouter();

  const language = getLangFromLocale(locale as string);
  const { showToast } = useCustomToast();

  const isNonTransactional = useMemo(() => isNonTransactionalCountry(locale as string), [locale]);
  const productUrl = useMemo(
    () => landingPageBaseUrl || `/product/${slug}/${variant?.id}`,
    [landingPageBaseUrl, slug, variant?.id],
  );

  //functions
  const productImage = useMemo(() => {
    const envVariable =
      process.env.NEXT_PUBLIC_BRAND_ID ?? process.env.NEXT_PUBLIC_FALLBACK_BRAND_ID ?? "magrabi";

    const ProdImageAttribute = variant?.attributes?.find(
      (item) =>
        item?.attribute?.slug === SaleorAttributes?.PRODUCT_IMAGE ||
        item?.attribute?.slug === SaleorAttributes?.CL_PRODUCT_IMAGE,
    );

    try {
      const variantImage = JSON.parse(ProdImageAttribute?.values[0]?.scoppable ?? "");
      return variantImage?.[envVariable].split(",")[0];
    } catch (err) {
      return NoImageFallback.src;
    }
  }, [variant]);

  const isVtoEnabled = () => {
    const VTO = variant?.attributes?.find((item) => item?.attribute?.slug === "vto_enabled");
    return VTO?.values[0].boolean;
  };

  const productSpecs = useMemo(
    () =>
      getSpecsForProductType(
        productType?.slug ?? "",
        variant?.attributes,
        product?.attributes ?? [],
        translations?.common,
        language,
        product?.id,
        productAttributeOverrides,
      ),
    [
      productType,
      variant,
      translations?.common,
      product?.attributes,
      language,
      product?.id,
      productAttributeOverrides,
    ],
  );

  const { ean, sku } = useMemo(() => {
    const ean = extractValueBySlug(language, item?.variant?.attributes ?? [], "sapEAN") as string;
    const sku = item?.variant?.sku;
    console?.log({ sku, ean });
    return { ean, sku };
  }, [item]);

  const SelectedVariantName = useMemo(() => {
    const VariantTitle = variant?.attributes?.find((item) => item?.attribute?.slug === "title");
    const envVariable =
      process.env.NEXT_PUBLIC_BRAND_ID ?? process.env.NEXT_PUBLIC_FALLBACK_BRAND_ID ?? "magrabi";
    if (language === "ar") {
      const ArabicVal = VariantTitle?.values[0].translation?.scoppable;
      const jsonObject = JSON?.parse(ArabicVal || "");
      return jsonObject?.[envVariable];
    } else {
      const EnglishVal = VariantTitle?.values[0]?.scoppable;
      const jsonObject = JSON?.parse(EnglishVal || "");
      return jsonObject?.[envVariable];
    }
  }, [language, product, variant?.attributes]);

  console.log(SelectedVariantName, "SelectedVariantName");

  const brandName = useMemo(
    () =>
      getValueBasedOnLanguage(
        language,
        product?.brand,
        "name",
        "name",
        product?.brand?.translation,
      ),
    [language, product?.brand],
  );

  const handleAddToCart = async () => {
    console.log("item", item);
    console.log("product", product);
    const response = await addToCartById(locale as string, variant?.id || "", {
      magrabiId: user?.magrabi_id,
    });

    if (response?.checkout?.lines) {
      showToast({
        imgUrl: productImage,
        text: translations?.common?.common_added_to_cart_toast_message ?? "",
        lang: language,
        redirectUrl: "/cart",
      });

      const count = getCartCount(response?.checkout?.lines ?? []) ?? cartCount;
      updateCartCount(count);

      await handlePostAnalytics({
        eventType: analyticEventTypes.ACTIONS,
        data: {
          target: analyticEvents.ADD_TO_CART,
          product: {
            ...{ ...item, variants: [variant] },
            name: SelectedVariantName,
            category: { name: product?.category?.name },
            brand: { name: brandName },
            hitIndex: index || 0,
            selectedQuantity: 1,
          },
          selectedVariantId: variant?.id,
          source,
          sourceId,
          buyFlow: analyticBuyFlows.QUICK_BUY,
        },
      });
    } else if (!response?.checkout && response && hasArrayElements(response?.errors)) {
      const [error] = response?.errors || [];
      let errorMessage = "";
      if (error?.code === cartErrors?.INSUFFICIENT_STOCK_ERROR_CODE) {
        errorMessage =
          translations?.common?.common_add_to_cart_insufficient_stock_toast_message ?? "";
      } else if (error?.code === cartErrors?.QUANTITY_GREATER_THAN_LIMIT_ERROR_CODE) {
        errorMessage =
          translations?.common?.common_add_to_cart_quantity_exceeds_checkout_limit_toast_message ??
          "";
      } else if (error?.code === cartErrors?.PRODUCT_UNAVAILABLE_FOR_PURCHASE_ERROR_CODE) {
        errorMessage =
          translations?.common?.common_product_unavailable_for_purchase_toast_message ?? "";
      } else {
        errorMessage = translations?.common?.common_add_to_cart_error_toast_message ?? "";
      }
      showToast({
        imgUrl: productImage,
        text: errorMessage,
        lang: language,
        redirectUrl: "/cart",
      });
    }
  };

  const handleGoToPdp = () => {
    router?.push(productUrl);
  };

  const handleFindStore = () => {
    router.push("/find-store");
  };

  const { buttonText, onClickAction } = useMemo(() => {
    const returnData = {
      buttonText: favoritesStaticText?.favorite_add_to_cart,
      onClickAction: () => {
        handleAddToCart();
      },
    };
    if (!Boolean(quantityAvailable)) {
      returnData.buttonText = favoritesStaticText?.favorite_notify_me_when_in_stock;
      returnData.onClickAction = () => setOpenNotifyInStock(true);
    } else if (
      applicableProductTypes.includes(productType?.slug || "") ||
      productType?.slug === ProductType.EYEGLASSES
    ) {
      returnData.buttonText = favoritesStaticText?.favorite_select_lens_and_purchase;
    } else if (isDefaultVariant && hasLensPower(productType?.slug)) {
      returnData.buttonText = favoritesStaticText?.favorites_view_product_button;
      returnData.onClickAction = handleGoToPdp;
    }
    if (isNonTransactional) {
      returnData.buttonText = translations?.pdp?.pdp_find_a_store_label || "Find a Store";
      returnData.onClickAction = handleFindStore;
    }
    return returnData;
  }, [
    quantityAvailable,
    productType?.slug,
    isDefaultVariant,
    isNonTransactional,
    favoritesStaticText?.favorite_add_to_cart,
    favoritesStaticText?.favorite_notify_me_when_in_stock,
    favoritesStaticText?.favorite_select_lens_and_purchase,
    favoritesStaticText?.favorites_view_product_button,
    translations?.pdp?.pdp_find_a_store_label,
  ]);

  const promotionName = useMemo(() => {
    const promotion = variant?.availablePromotions?.[0]?.promotion;
    return getPromotionBadgeName(promotion, language);
  }, [language, variant]);

  const wishlistPriceInfo = useMemo(() => {
    const [promotionDetails] = variant?.availablePromotions || [];
    const undiscountedPrice =
      variant?.pricing?.costPrice?.gross?.amount ??
      variant?.pricing?.priceUndiscounted?.gross?.amount ??
      0;
    const salePrice =
      variant?.pricing?.price?.gross?.amount ??
      variant?.pricing?.priceUndiscounted?.gross?.amount ??
      0;

    return buildProductPriceInfo(undiscountedPrice, salePrice, promotionDetails, variant?.pricing);
  }, [variant?.availablePromotions, variant?.pricing]);

  const deleteWishlistLine = async (variantId: string) => {
    if (wishlistId) {
      const response = await deleteWishlistLineItem(wishlistId, variantId);
      if (response?.wishlist) {
        removeProductFromWishlist(variantId);
        removeProductFromLocalWishlist(response?.wishlist);
      }
    }
  };

  const handleNotifyMe = async (email: string) => {
    const channel = getChannelIdInClient(locale as string);
    const response = await notifyOutOfStockProduct(
      email,
      variant?.id,
      channel,
      productUrl,
      language,
    );
    if (response) {
      setOpenNotifyInStock(false);
    }
    if (response?.errors?.length > 0) {
      //TO DO when proper error handling with error code is handled in backend check for error code
      if (response?.errors[0].message === "Subscription already exists?.") {
        showToast({
          text: pdpContent?.pdp_subscription_already_exists_for_this_email_message,
          imgUrl: productImage ?? NoImageFallback,
          lang: language,
        });

        return pdpContent?.pdp_subscription_already_exists_for_this_email_message
          ? pdpContent?.pdp_subscription_already_exists_for_this_email_message
          : "Subscription already exists for this email";
      } else {
        showToast({
          text: pdpContent?.pdp_something_bad_happened_message,
          imgUrl: productImage ?? NoImageFallback,
          lang: language,
        });

        return pdpContent?.pdp_something_bad_happened_message
          ? pdpContent?.pdp_something_bad_happened_message
          : "Something bad happened";
      }
    } else {
      showToast({
        text: pdpContent?.pdp_subscription_successful_message,
        imgUrl: productImage ?? NoImageFallback,
        lang: language,
      });

      return pdpContent?.pdp_subscription_successful_message
        ? pdpContent?.pdp_subscription_successful_message
        : "Subscription successful";
    }
  };

  return (
    <Fragment>
      <div className="relative flex w-full gap-3 bg-white p-4 md:gap-6">
        <div className="relative hidden aspect-[130/157] h-[157px] items-center justify-center rounded-lg md:flex">
          <CLink href={productUrl}>
            <CldImage
              src={getCloudinaryURL(productImage) ?? NoImageFallback.src}
              alt={"productImage"}
              height={157}
              width={200}
              className="h-auto w-full"
            />
          </CLink>

          <div className="absolute left-0 top-0">
            {quantityAvailable ? (
              <>
                {hasArrayElements(badgeList) && (
                  <ProductBadge
                    variant={badgeList?.[0].name}
                    title={
                      getFieldValue(
                        language,
                        badgeList?.[0].name,
                        badgeList[0]?.translation?.name,
                      ) || ""
                    }
                  />
                )}
              </>
            ) : (
              <div className="bg-primary-3 px-2 py-1 text-xs font-semibold text-white">
                {pdpContent?.pdp_out_of_stock_label ?? "Out of Stock"}
              </div>
            )}
          </div>
          {isVtoEnabled() && (
            <div
              role="button"
              className="absolute bottom-1 left-1 cursor-pointer rounded-[21px] bg-white px-2 py-1 text-xs font-semibold shadow-[0_1?.5px_4px_rgba(234,234,234,0?.5)]"
              onClick={() => setVtoOpen(true)}
            >
              {translations?.common?.common_try_them_on_label ?? " Try Them On"}
            </div>
          )}
        </div>
        <div className="flex w-full flex-col">
          <div className="flex gap-3 border-b border-tertiary-9 pb-3">
            <div className="relative flex aspect-[130/157] h-[157px] items-center justify-center rounded-lg md:hidden">
              <CLink href={productUrl}>
                <CldImage
                  src={productImage ?? NoImageFallback.src}
                  alt={"productImage"}
                  height={157}
                  width={131}
                  className="h-auto w-full"
                />
              </CLink>

              <div className="absolute left-0 top-0">
                {quantityAvailable ? (
                  <>
                    {hasArrayElements(badgeList) && (
                      <ProductBadge
                        variant={badgeList?.[0].name}
                        title={
                          getFieldValue(
                            language,
                            badgeList?.[0].name,
                            badgeList[0]?.translation?.name,
                          ) || ""
                        }
                      />
                    )}
                  </>
                ) : (
                  <div className="bg-primary-3 px-2 py-1 text-xs font-semibold text-white">
                    {pdpContent?.pdp_out_of_stock_label ?? "Out of Stock"}
                  </div>
                )}
              </div>
              {isVtoEnabled() && (
                <div className="absolute bottom-2 left-1 cursor-pointer rounded-[21px] bg-white px-2 py-1 text-xs font-semibold shadow-[0_1?.5px_4px_rgba(234,234,234,0?.5)]">
                  {translations?.common?.common_try_them_on_label ?? " Try Them On"}
                </div>
              )}
            </div>
            <div>
              <p className="pb-1 text-sm font-semibold">{brandName}</p>
              <p className="pb-[10px] text-sm font-normal text-tertiary-6">{SelectedVariantName}</p>
              <CLink href={productUrl}>
                <div className="flex flex-col gap-[2px]">
                  {productSpecs?.map((specs, idx) => (
                    <p key={idx} className="pb-1 text-sm font-semibold">
                      {specs?.label}: <span className="font-normal"> {specs?.value}</span>
                    </p>
                  ))}
                </div>
                {promotionName && (
                  <div className="text-xs font-normal text-secondary-1 md:text-sm">
                    <RenderCurrencyInText text={promotionName} />
                  </div>
                )}
              </CLink>

              {/* <div className="flex flex-col gap-[2px]">
                {hasArrayElements(variant?.availablePromotions) &&
                  variant?.availablePromotions?.map((item) => (
                    <p className="text-xs text-secondary-1 md:text-[13px]">
                      {getFieldValue(
                        language,
                        item?.promotion?.name,
                        item?.promotion?.translation?.name,
                      )}
                    </p>
                  ))}
              </div> */}
            </div>
            <div
              className="absolute right-2 top-2 flex h-6 w-6 cursor-pointer items-center justify-center"
              onClick={(prev) => {
                setOpenRemoveFavorite(true);
              }}
            >
              <CloseIcon height={12} width={12} />
            </div>
          </div>

          <div className="flex items-start justify-between pt-4 md:items-center">
            <div className="flex flex-col text-sm md:flex-row md:gap-1">
              <ProductPrice
                priceInfo={wishlistPriceInfo}
                locale={locale as string}
                translations={translations}
              />
              {ProductType?.EYEGLASSES === productType?.slug ? (
                <p className="font-semibold md:font-normal">
                  {favoritesStaticText?.favorite_excluding_lens_label}
                </p>
              ) : (
                ""
              )}
            </div>
            <div className="flex flex-col items-center gap-5 text-sm md:flex-row md:gap-7">
              {/* <p className="order-2 font-semibold underline md:order-1">Buy frame only</p> */}
              {buttonText === favoritesStaticText?.favorite_select_lens_and_purchase && product ? (
                <LensConfiguratorWrapper
                  locale={locale as string}
                  product={product}
                  variant={variant}
                  hitIndex={index}
                  toastToggle={() => ({})}
                  translations={translations}
                  lensConfiguratorImageURL={productImage}
                >
                  <Button className="order-1 min-w-[150px] py-[10px] md:order-2 md:py-3">
                    {buttonText}
                  </Button>
                </LensConfiguratorWrapper>
              ) : (
                <Button
                  variant="contained"
                  className="order-1 min-w-[150px] py-[10px] md:order-2 md:py-3"
                  onClick={onClickAction}
                >
                  {buttonText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      <RemoveFavorite
        open={openRemoveFavorite}
        imgURL={productImage || ""}
        onCancel={() => setOpenRemoveFavorite(false)}
        onAccept={() => deleteWishlistLine(variant?.id)}
        translations={translations}
      />
      <NotifyInStock
        open={openNotifyInStock}
        imgURL={productImage || ""}
        onCancel={() => setOpenNotifyInStock(false)}
        onAccept={handleNotifyMe}
        translations={translations}
      />

      <VtoModal
        isOpen={vtoOpen}
        frameId={{
          ean,
          sku,
        }}
        toggle={() => setVtoOpen(false)}
      />
    </Fragment>
  );
};

export default FavoritesItemCard;
